import { useRef } from 'preact/hooks';

import scheduleIdleCallback from 'Modules/scheduleIdleCallback';

// Local "deps" alias mirroring Preact's `Inputs` (= `readonly unknown[]`)
// but kept mutable since the resolver below assigns into the tuple before storing.
type IdleDeps = unknown[];

interface IdleOpts {
  deps?: IdleDeps;
  opts?: IdleRequestOptions;
}

export default function useIdleEffect(
  func: () => (() => void) | void,
  ...effectOpts: [deps?: IdleDeps, opts?: IdleRequestOptions] | [opts?: IdleRequestOptions]
): void {
  const { deps, opts } = resolveOpts(effectOpts);

  const flushRef = useRef<(() => void) | undefined>();
  const depsRef = useRef<IdleDeps>([]);

  if (deps && areDepsEqual(depsRef.current, deps)) {
    return;
  }

  depsRef.current = deps ?? [];
  const flush = flushRef.current;
  if (typeof flush === 'function') {
    flush();
  }
  scheduleIdleCallback(() => {
    const result = func();
    flushRef.current = typeof result === 'function' ? result : undefined;
  }, opts);
}

function resolveOpts(
  opts: [deps?: IdleDeps, opts?: IdleRequestOptions] | [opts?: IdleRequestOptions],
): IdleOpts {
  if (!opts.length) {
    return {};
  }
  const resolved: IdleOpts = {};
  if (Array.isArray(opts[0])) {
    resolved.deps = opts[0]; // eslint-disable-line prefer-destructuring
  } else if (typeof opts[0] === 'object') {
    resolved.opts = opts[0]; // eslint-disable-line prefer-destructuring
  } else {
    console.error(
      'Wrong optional arguments for requestIdleCallback: ' +
        'expected array of deps or options for idleCallback or both but received ',
      opts,
    );
  }
  if (resolved.deps && typeof opts[1] === 'object') {
    resolved.opts = opts[1]; // eslint-disable-line prefer-destructuring
  }
  return resolved;
}

function areDepsEqual(prevDeps: IdleDeps, newDeps: IdleDeps) {
  return newDeps.every((dep, i) => Object.is(dep, prevDeps[i]));
}
